// The unranked play aids' automatic answers (design 8 P3), from the player's own settings: a pure function
// of the view, so the session can apply it to each new prompt once.

import type { Action } from "../pb/mj/record/v1/record_pb";
import { ActionType } from "../pb/mj/record/v1/record_pb";
import { PromptKind } from "../pb/mj/protocol/v1/protocol_pb";
import type { View } from "./view";

export interface PlaySettings {
  auto_win: boolean;
  skip_calls: boolean;
  tsumogiri: boolean;
  auto_sort: boolean;
  sound: boolean;
  volume: number;
  feel: Feel; // how tiles move (design 10 §7)
  haptics: boolean; // a light pulse as your tile lands, your calls, wins (design 10 §8)
  drawn: "apart" | "on_top"; // where the drawn tile lies; every seat sees it (design 10 T6)
  view: BoardView; // how the board is laid out (design 10 §6)
  angle: number; // how far the board leans back from straight above, in degrees (design 10 T15)
  experience: Experience; // how well they know riichi, asked once on the first game (design 14 §9.3)
  hints: boolean; // the first games' hints on the table (design 14 §9.4)
  hints_seen: string[]; // those this account has been shown
  table_ready: boolean; // the sound when a ranked table forms (design 14 §6.3)
}

export type Experience = "" | "new" | "rules" | "elsewhere";

export type BoardView = "auto" | "top" | "compact";
export const VIEWS: BoardView[] = ["auto", "top", "compact"];

/** The layout a setting comes to on this screen: on a phone, either way up, the larger tiles of the view without the wall. */
export function boardView(v: BoardView | undefined): "top" | "compact" {
  if (v && v !== "auto") return v;
  return typeof innerWidth === "number" && Math.min(innerWidth, innerHeight) < 500 ? "compact" : "top";
}
export const MAX_ANGLE = 60; // platform/internal/store/settings.go

export type Feel = "weighty" | "brisk" | "gentle" | "instant";
export const FEELS: Feel[] = ["weighty", "brisk", "gentle", "instant"];

export const DEFAULT_SETTINGS: PlaySettings = { auto_win: false, skip_calls: false, tsumogiri: false, auto_sort: true, sound: true, volume: 60, feel: "weighty", haptics: true, drawn: "apart", view: "auto", angle: 20, experience: "", hints: false, hints_seen: [], table_ready: true };

const WINS = new Set([ActionType.RON, ActionType.TSUMO]);
const CALLS = new Set([ActionType.CHI, ActionType.PON, ActionType.OPEN_KAN]);

export const RIICHI_DISCARD_MS = 700;

/** In riichi, with nothing but the drawn tile to discard: that discard. */
export function riichiDiscard(v: View): Action | null {
  const p = v.prompt;
  if (!p || p.sent || p.rejected || !v.seats[v.seat]?.riichi || p.prompt.kind !== PromptKind.AFTER_DRAW) return null;
  const legal = p.prompt.legal;
  return legal.length === 1 && legal[0]!.type === ActionType.DISCARD ? legal[0]! : null;
}

/** What the aids answer for the open prompt, or null to leave it to the player. */
export function autoAction(v: View, s: PlaySettings): Action | null {
  const p = v.prompt;
  if (!p || p.sent || p.rejected) return null;
  const legal = p.prompt.legal;
  if (s.auto_win) {
    const win = legal.find((a) => WINS.has(a.type));
    if (win) return win;
  }
  const window = p.prompt.kind === PromptKind.WINDOW;
  if (s.skip_calls && window && !legal.some((a) => WINS.has(a.type)) && legal.some((a) => CALLS.has(a.type))) {
    return legal.find((a) => a.type === ActionType.PASS) ?? null;
  }
  if (s.tsumogiri && p.prompt.kind === PromptKind.AFTER_DRAW && legal.every((a) => a.type === ActionType.DISCARD)) {
    const drawn = v.seats[v.seat]?.drawn;
    return legal.find((a) => a.tiles[0] === drawn) ?? null;
  }
  return null;
}
